import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Logger from '@medflex/utils/Logger';
import getUserType from '@medflex/utils/getUserType';
import { Route, Switch, useLocation } from 'react-router-dom';
import LoadingView from '../../LoadingView';
import UserManagerContext from '../UserManagerContext';
import SilentRenew from '../SilentRenew';
import SignOut from '../SignOut';
import parseJwt from './parseJsonWebToken';

const TWO_MINUTES_IN_MS = 2 * 60 * 1000;

function isTokenExpired(token) {
  const jwt = parseJwt(token);
  if (!jwt) {
    return true;
  }
  return new Date(jwt.exp * 1000) - new Date() < 0;
}

const DesktopUserManagerLegacy = ({ children, onSessionLoaded, externalExpired }) => {
  const location = useLocation();
  const { t } = useTranslation('components');
  const [currentAccessToken, setCurrentAccessToken] = useState(null);

  const setSession = useCallback(
    session => {
      Logger.log('[DesktopUserManagerLegacy] - new session loaded', session);
      const userType = getUserType(session.profile.azp);
      Logger.log(`userType: ${userType}`);
      Logger.log(`expired: ${session.expired}`);
      Logger.log(`expires: ${new Date(session.expires_at * 1000)}`);
      onSessionLoaded({ ...session, token_type: 'Bearer', userType });
    },
    [onSessionLoaded],
  );

  const logout = useCallback(() => {
    setCurrentAccessToken(null);
    window.electron.authentication.logout();
  }, []);

  const retrieveAccessToken = useCallback(async () => {
    const accessToken = await window.electron.authentication.getAccessToken();
    if (!accessToken) {
      logout();
    } else {
      setCurrentAccessToken(accessToken);
    }
  }, [logout]);

  const refreshAccessToken = useCallback(async () => {
    const accessToken = await window.electron.authentication.refreshAccessToken();
    if (!accessToken) {
      Logger.error('[DesktopUserManagerLegacy] - Refresh access token failed.', new Error().stack);
      logout();
    } else {
      setCurrentAccessToken(accessToken);
    }
  }, [logout]);

  useEffect(() => {
    if (!currentAccessToken) {
      retrieveAccessToken();
    } else if (isTokenExpired(currentAccessToken)) {
      refreshAccessToken();
    } else {
      Logger.log('[DesktopUserManagerLegacy] - user session changed');
      const jwt = parseJwt(currentAccessToken);

      if (!jwt) {
        Logger.error('[DesktopUserManagerLegacy] - Failed parsing jwt. Unhandled error!');
      }

      const { typ, userId, azp, exp } = jwt || {}; // workaround for unhandled error
      setSession({
        access_token: currentAccessToken,
        token_type: typ,
        expires_at: exp,
        expires_in: exp,
        expired: isTokenExpired(currentAccessToken),
        profile: { userId, azp },
      });

      const expiresInMS = Math.max(new Date(exp * 1000) - new Date(), 0);
      const experingInMs = Math.max(expiresInMS - TWO_MINUTES_IN_MS, 0);

      // Refresh the access token 2 minutes before it is going to be expired
      const expringTimeout = setTimeout(() => {
        Logger.log('[DesktopUserManagerLegacy] - access token expiring -> refreshing it');
        refreshAccessToken();
      }, experingInMs);

      const expiredTimout = setTimeout(() => {
        Logger.warn('[DesktopUserManagerLegacy] - access token expired.', new Error().stack);
        refreshAccessToken();
      }, expiresInMS);

      return () => {
        clearTimeout(expringTimeout);
        clearTimeout(expiredTimout);
      };
    }
    return undefined;
  }, [currentAccessToken, refreshAccessToken, retrieveAccessToken, setSession]);

  useEffect(() => {
    if (externalExpired) {
      Logger.warn(
        '[DesktopUserManagerLegacy] -> external source (websocket) reported expired token; trigger refresh token',
      );

      refreshAccessToken();
    }
  }, [externalExpired, refreshAccessToken]);

  const managerValues = useMemo(
    () => ({ signOutRedirect: logout, signinSilentCallback: refreshAccessToken }),
    [logout, refreshAccessToken],
  );

  return (
    <UserManagerContext.Provider value={managerValues}>
      <Switch>
        <Route exact path="*/silent-renew" component={SilentRenew} />
        <Route exact path="/abmelden" component={SignOut} />
        <Route>
          {currentAccessToken && !isTokenExpired(currentAccessToken) ? (
            children
          ) : (
            <LoadingView
              title={t('UserManager.LoadingView.title')}
              message={t('UserManager.LoadingView.message')}
            />
          )}
        </Route>
      </Switch>
    </UserManagerContext.Provider>
  );
};

DesktopUserManagerLegacy.propTypes = {
  children: PropTypes.node,
  onSessionLoaded: PropTypes.func,
  externalExpired: PropTypes.bool,
};

DesktopUserManagerLegacy.defaultProps = {
  onSessionLoaded: () => {},
  children: null,
  externalExpired: false,
};

export default DesktopUserManagerLegacy;
